<template>
    <v-row no-gutters class="pa-2">
        <v-col cols="9">
            <!-- Back button -->
            <v-btn-back @click="back()" />

            <!-- Zoom action -->
            <v-btn-icon
                icon="$oss-zoom-out"
                :disabled="MenuOptions.zoomButtons.isZoomOutDisabled"
                @click.stop="zoomOut()"
                v-if="MenuOptions.zoomButtons.isDisplay"
            />
            <v-btn-icon
                icon="$oss-focus"
                :disabled="MenuOptions.zoomButtons.isResetZoomDisabled"
                @click.stop="resetZoom()"
                v-if="MenuOptions.zoomButtons.isDisplay"
            />
            <v-btn-icon
                icon="$oss-zoom-in"
                :disabled="MenuOptions.zoomButtons.isZoomInDisabled"
                @click.stop="zoomIn()"
                v-if="MenuOptions.zoomButtons.isDisplay"
            />

            <!-- OCR action -->
            <v-btn-icon
                icon="$oss-eye"
                :disabled="MenuOptions.OCRButtons.disabled"
                :loading="MenuOptions.OCRButtons.isLoading"
                @click.stop="ocrChanged()"
                v-if="MenuOptions.OCRButtons.isDisplay"
            />

            <!-- Split document action -->
            <v-btn-icon
                icon="$fa-cut"
                :disabled="MenuOptions.cutButton.disabled"
                class="ml-1"
                @click.stop="cut()"
                v-if="MenuOptions.cutButton.isDisplay"
            />
            <!-- Rotation action -->
            <v-btn-icon
                icon="$fa-undo"
                size="x-small"
                @click.stop="rotateLeft()"
                :disabled="MenuOptions.RotateDocument.disabledLeftRotation"
                v-if="MenuOptions.RotateDocument.isDisplay"
            />
            <v-btn-icon
                icon="$fa-redo"
                size="x-small"
                @click.stop="rotateRight()"
                :disabled="MenuOptions.RotateDocument.disabledRightRotation"
                v-if="MenuOptions.RotateDocument.isDisplay"
            />
            <!-- Title -->
            <h3 v-if="MenuOptions.title.isDisplay">{{ MenuOptions.title.text }}</h3>
        </v-col>
        <v-col cols="3" class="d-flex justify-end">
            <!-- Download outstide dotMenu action -->
            <v-btn-icon
                icon="$oss-download"
                class="mx-1"
                @click="download()"
                :disabled="MenuOptions.downloadButton.disabled"
                v-if="MenuOptions.downloadButton.isDisplay"
            />

            <!-- Edit outside dotMenu action -->
            <v-tooltip
                v-if="
                    MenuOptions.editButton.isDisplay && MenuOptions?.editButton?.HTMLMessage?.length
                "
                class="pt-4 px-4"
                location="bottom"
            >
                <template v-slot:activator="{ props }">
                    <div style="height: fit-content" v-bind="props">
                        <v-btn-icon
                            icon="$oss-edit"
                            class="mx-1"
                            @click="edit()"
                            :disabled="MenuOptions.editButton.disabled"
                            v-bind="props"
                        />
                    </div>
                </template>
                <div v-html="MenuOptions.editButton.HTMLMessage" />
            </v-tooltip>
            <template v-else-if="MenuOptions.editButton.isDisplay">
                <v-btn-icon
                    icon="$oss-edit"
                    @click="edit()"
                    :disabled="MenuOptions.editButton.disabled"
                />
            </template>

            <!-- Display mode action -->
            <v-btn-toggle
                v-model="_displayMode"
                rounded="xl"
                class="mt-1"
                density="compact"
                divided
                variant="outlined"
                color="secondary"
                mandatory
                v-if="MenuOptions.displayModeButtons.isDisplay"
            >
                <!-- Display table -->
                <v-btn
                    :value="'table'"
                    icon="$fa-table"
                    @click="displayTable()"
                    v-if="MenuOptions.displayModeButtons.isTableModeDisplay"
                />

                <!-- Display accounting lines -->
                <v-btn
                    :value="'display-accounting-lines'"
                    icon="$fa-list"
                    @click="displayAccountingLine()"
                    v-if="MenuOptions.displayModeButtons.isAccountingLineModeDisplay"
                />

                <!-- Display file/pdf -->
                <v-btn
                    :value="'display-pdf'"
                    icon="$oss-file"
                    @click="displayFile()"
                    :disabled="MenuOptions.displayModeButtons.isPDFModeDisabled"
                    :readonly="MenuOptions.displayModeButtons.isPDFModeDisabled"
                    v-if="MenuOptions.displayModeButtons.isPDFModeDisplay"
                />
            </v-btn-toggle>

            <!-- Duplicate action -->
            <v-btn
                rounded
                elevation="0"
                @click.stop="duplicate()"
                size="small"
                class="px-2 mt-2"
                :disabled="MenuOptions.duplicate.disabled"
                v-if="MenuOptions.duplicate.isDisplay"
            >
                {{ MenuOptions.duplicate.title }}
            </v-btn>

            <!-- DotMenu action -->
            <v-menu offset-y v-if="MenuOptions.dotMenu.isDisplay">
                <template v-slot:activator="{ props }">
                    <v-btn-icon icon="$dots_vertical" v-bind="props" />
                </template>
                <v-list nav rounded="xl">
                    <!-- Display file -->
                    <v-list-item
                        @click="displayFile()"
                        v-if="MenuOptions.dotMenu.document.isDisplay"
                        :disabled="MenuOptions.dotMenu.document.disabled"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.document.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-file</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Display accounting line -->
                    <v-list-item
                        @click="displayAccountingLine()"
                        v-if="MenuOptions.dotMenu.accountingLine.isDisplay"
                        :disabled="MenuOptions.dotMenu.accountingLine.disabled"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.accountingLine.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-accounting-lines</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Display items -->
                    <v-list-item
                        @click="displayItems()"
                        v-if="MenuOptions.dotMenu.items.isDisplay"
                        :disabled="MenuOptions.dotMenu.items.disabled"
                    >
                        <v-list-item-title>{{ MenuOptions.dotMenu.items.title }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-eye</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Download inside dotMenu -->
                    <v-list-item
                        v-if="MenuOptions.dotMenu.download.isDisplay"
                        @click="download()"
                        :disabled="MenuOptions.dotMenu.download.disabled"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.download.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-download</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Upload -->
                    <v-list-item
                        @click="upload()"
                        v-if="MenuOptions.dotMenu.upload.isDisplay"
                        :disabled="MenuOptions.dotMenu.upload.disabled"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.upload.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon>$oss-upload</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Edit inside dotMenu -->
                    <v-tooltip
                        v-if="
                            MenuOptions.dotMenu.edit.isDisplay &&
                            MenuOptions.dotMenu.edit.HTMLMessage.length
                        "
                        class="pt-4 px-4"
                        location="bottom"
                    >
                        <template v-slot:activator="{ props }">
                            <div style="height: fit-content" v-bind="props">
                                <v-btn-icon
                                    icon="$oss-edit"
                                    class="mx-1"
                                    @click="edit()"
                                    :disabled="MenuOptions.dotMenu.edit.disabled"
                                    v-bind="props"
                                />
                            </div>
                        </template>
                        <div v-html="MenuOptions.dotMenu.edit.HTMLMessage" />
                    </v-tooltip>
                    <v-list-item
                        @click="edit()"
                        v-else-if="MenuOptions.dotMenu.edit.isDisplay"
                        :disabled="MenuOptions.dotMenu.edit.disabled"
                    >
                        <v-list-item-title>{{ MenuOptions.dotMenu.edit.title }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-edit</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Update MTAE -->
                    <TaxFormEDIReport
                        v-if="MenuOptions.dotMenu.updateMTAE.isDisplay"
                        :disabled="MenuOptions.dotMenu.updateMTAE.disabled"
                        :taxFormId="MenuOptions.dotMenu.updateMTAE.taxformId"
                        :isDeclaration="MenuOptions.dotMenu.updateMTAE.isDeclaration"
                    >
                        <template #default="{ statusOk, mtaeProof }">
                            <v-list-item :disabled="!statusOk" @click="mtaeProof">
                                <v-list-item-title>{{
                                    MenuOptions.dotMenu.updateMTAE.title
                                }}</v-list-item-title>
                                <template v-slot:append>
                                    <v-icon size="small">$oss-download</v-icon>
                                </template>
                            </v-list-item>
                        </template>
                    </TaxFormEDIReport>

                    <v-list-item
                        v-if="MenuOptions.dotMenu.update.isDisplay"
                        :disabled="MenuOptions.dotMenu.update.disabled"
                        @click="update()"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.update.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$fa-redo-alt</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Regenerate -->
                    <v-list-item
                        @click="regenerate()"
                        :disabled="MenuOptions.dotMenu.regenerate.disabled"
                        v-if="MenuOptions.dotMenu.regenerate.isDisplay"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.regenerate.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$fa-redo-alt</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Analyse -->
                    <v-list-item
                        @click="analyse()"
                        v-if="MenuOptions.dotMenu.analyse.isDisplay"
                        :disabled="MenuOptions.dotMenu.analyse.disabled"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.analyse.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-scan</v-icon>
                        </template>
                    </v-list-item>

                    <!-- Activity -->
                    <v-list-item
                        @click="activity()"
                        :disabled="MenuOptions.dotMenu.activity.disabled"
                        v-if="MenuOptions.dotMenu.activity.isDisplay"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.activity.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-activity</v-icon>
                        </template>
                    </v-list-item>

                    <v-divider class="pb-2" />

                    <!-- Delete -->
                    <v-tooltip
                        v-if="
                            MenuOptions.dotMenu.delete.isDisplay &&
                            MenuOptions.dotMenu.delete.HTMLMessage
                        "
                        location="bottom"
                    >
                        <template v-slot:activator="{ props }">
                            <div style="height: fit-content" v-bind="props">
                                <v-list-item
                                    @click="deleteDocument()"
                                    :disabled="MenuOptions.dotMenu.delete.disabled"
                                    color="error"
                                    :active="!MenuOptions.dotMenu.delete.disabled"
                                >
                                    <v-list-item-title>{{
                                        MenuOptions.dotMenu.delete.title
                                    }}</v-list-item-title>
                                    <template v-slot:append>
                                        <v-icon size="small">$oss-trash</v-icon>
                                    </template>
                                </v-list-item>
                            </div>
                        </template>
                        <div v-html="MenuOptions.dotMenu.delete.HTMLMessage" />
                    </v-tooltip>
                    <v-list-item
                        v-else-if="MenuOptions.dotMenu.delete.isDisplay"
                        @click="deleteDocument()"
                        :disabled="MenuOptions.dotMenu.delete.disabled"
                        :color="MenuOptions.dotMenu.delete.disabled ? '' : 'error'"
                        :active="!MenuOptions.dotMenu.delete.disabled"
                    >
                        <v-list-item-title>{{
                            MenuOptions.dotMenu.delete.title
                        }}</v-list-item-title>
                        <template v-slot:append>
                            <v-icon size="small">$oss-trash</v-icon>
                        </template>
                    </v-list-item>
                </v-list>
            </v-menu>
        </v-col>
    </v-row>
    <v-row no-gutters class="px-2 pb-2" v-show="_selectedText">
        <v-col>
            <!-- Selected Text -->
            <v-text-field-filled
                v-model="_selectedText"
                id="selectedText"
                hide-details
                class="ml-2 small-prepend-icon"
                append-inner-icon="$oss-close"
                @click:append-inner="clearSelectedText()"
                v-if="MenuOptions.isSelectedText"
            />
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { MenuDisplayOptions } from "@/types/documentMenu.types";

import { Component, Vue, toNative, Prop, Model, Watch } from "vue-facing-decorator";
import { TaxFormEDIReport } from "../taxForm/TaxFormEDIReport.vue";

@Component({
    emits: [
        "back",
        "zoom-in",
        "reset-zoom",
        "zoom-out",
        "ocr-changed",
        "rotate-left",
        "rotate-right",
        "clear-selected-text",
        "download",
        "edit",
        "display-table",
        "display-file",
        "display-accounting-line",
        "display-item",
        "analyse",
        "activity",
        "delete",
        "upload",
        "duplicate",
        "update",
        "regenerate",
    ],
    components: {
        TaxFormEDIReport,
    },
})
export class DocumentMenu extends Vue {
    // OCR Button Prop
    @Model({ default: "", name: "selectedText" }) _selectedText: string;

    // Display mode button Prop
    @Model({ default: "", name: "displayMode" }) _displayMode: string;

    // Display menu button Prop
    @Prop() MenuOptions: MenuDisplayOptions;

    back() {
        this.$emit("back");
    }

    zoomIn() {
        this.$emit("zoom-in");
    }

    resetZoom() {
        this.$emit("reset-zoom");
    }

    zoomOut() {
        this.$emit("zoom-out");
    }

    ocrChanged() {
        this.$emit("ocr-changed");
    }

    rotateLeft() {
        this.$emit("rotate-left");
    }

    rotateRight() {
        this.$emit("rotate-right");
    }

    clearSelectedText() {
        this.$emit("clear-selected-text");
    }

    cut() {
        this.$emit("cut");
    }

    download() {
        this.$emit("download");
    }

    edit() {
        this.$emit("edit");
    }

    displayTable() {
        this.$emit("display-table");
    }

    displayFile() {
        this.$emit("display-file");
    }

    displayAccountingLine() {
        this.$emit("display-accounting-line");
    }

    displayItems() {
        this.$emit("display-item");
    }

    analyse() {
        this.$emit("analyse");
    }

    activity() {
        this.$emit("activity");
    }

    deleteDocument() {
        this.$emit("delete");
    }

    upload() {
        this.$emit("upload");
    }

    duplicate() {
        this.$emit("duplicate");
    }

    update() {
        this.$emit("update");
    }

    updateMTAE() {
        this.$emit("update-mtae");
    }

    regenerate() {
        this.$emit("regenerate");
    }
}

export default toNative(DocumentMenu);
</script>
